import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { ChevronLeft, Star, Plus, ArrowRight, Sparkles, MoreVertical, X, Trash2, Check, History } from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import AvatarTeacher from "@/components/language/AvatarTeacher";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { LIVE_REQUIRES_PREMIUM } from "@/lib/liveAccess";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useLanguageLearning, type LanguageProfile, type LangCode, type LanguageSessionRow } from "@/hooks/useLanguageLearning";
import { LanguageDialog as AgendaBottomSheet } from '@/components/language/LanguageDialog';
import { LanguageClassPlanner } from '@/components/language/LanguageClassPlanner';
import { selectReviewWords } from '@/lib/language/practice';
import { LanguageState } from "@/components/language/LanguageState";
import LanguageActionModal from "@/components/language/LanguageActionModal";
import LanguagePendingRecoveryPanel from "@/components/language/LanguagePendingRecoveryPanel";
import { parseLanguageActionPrefill, type LanguageActionPrefill, type LanguageFields, type LanguageResource, type LanguageTarget } from "@/lib/languageActionExecutor";
import { ModuleHistoryList } from "@/components/history/ModuleHistoryList";

/* ─── Language metadata ─── */
const ALL_LANGUAGES: { id: string; label: string; emoji: string; bg: string; accent: string; accentBorder: string }[] = [
  { id: "en", label: "English", emoji: "\u{1F1EC}\u{1F1E7}", bg: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=75", accent: "hsl(220 60% 50% / 0.08)", accentBorder: "hsl(220 60% 50% / 0.35)" },
  { id: "pt", label: "Português", emoji: "\u{1F1E7}\u{1F1F7}", bg: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=400&q=75", accent: "hsl(120 50% 40% / 0.08)", accentBorder: "hsl(120 50% 40% / 0.35)" },
  { id: "nl", label: "Nederlands", emoji: "\u{1F1F3}\u{1F1F1}", bg: "https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&q=75", accent: "hsl(25 80% 50% / 0.08)", accentBorder: "hsl(25 80% 50% / 0.35)" },
  { id: "es", label: "Español", emoji: "\u{1F1EA}\u{1F1F8}", bg: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&q=75", accent: "hsl(0 70% 50% / 0.08)", accentBorder: "hsl(0 70% 50% / 0.35)" },
  { id: "fr", label: "Français", emoji: "\u{1F1EB}\u{1F1F7}", bg: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&q=75", accent: "hsl(220 70% 50% / 0.08)", accentBorder: "hsl(220 70% 50% / 0.35)" },
  { id: "de", label: "Deutsch", emoji: "\u{1F1E9}\u{1F1EA}", bg: "https://images.unsplash.com/photo-1560969184-10fe8719e047?w=400&q=75", accent: "hsl(45 80% 50% / 0.08)", accentBorder: "hsl(45 80% 50% / 0.35)" },
  { id: "it", label: "Italiano", emoji: "\u{1F1EE}\u{1F1F9}", bg: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&q=75", accent: "hsl(140 50% 40% / 0.08)", accentBorder: "hsl(140 50% 40% / 0.35)" },
  { id: "ja", label: "日本語", emoji: "\u{1F1EF}\u{1F1F5}", bg: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=75", accent: "hsl(0 70% 50% / 0.08)", accentBorder: "hsl(0 70% 50% / 0.35)" },
];

const LEVEL_LABELS: Record<string, string> = { beginner: "A1-A2", intermediate: "B1-B2", advanced: "C1-C2" };
const LEVELS: ("beginner" | "intermediate" | "advanced")[] = ["beginner", "intermediate", "advanced"];
const TIME_OPTIONS = [5, 10, 15, 30];

/* ─── Particles ─── */
const PARTICLES = [
  { left: "10%", top: "15%", w: 2, color: "#00e5ff", dur: 10, yD: -14, xD: 5 },
  { left: "80%", top: "28%", w: 1.5, color: "hsl(265 60% 65%)", dur: 12, yD: -10, xD: -4 },
  { left: "42%", top: "50%", w: 2.5, color: "#00e5ff", dur: 9, yD: -16, xD: 3 },
  { left: "25%", top: "70%", w: 1.8, color: "hsl(265 60% 65%)", dur: 11, yD: -12, xD: -6 },
  { left: "68%", top: "40%", w: 2, color: "#00e5ff", dur: 13, yD: -15, xD: 4 },
];
const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

/* ═══ Component ═══ */
const LanguageContent: React.FC<{ learning: ReturnType<typeof useLanguageLearning> }> = ({ learning }) => {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    profiles, progress, profileLoading, deleteProfile, refresh,
    getVocabularyForLanguage, getSessionsHistory,
  } = learning;

  const [editProfile, setEditProfile] = useState<LanguageProfile | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false), [saveError, setSaveError] = useState(false), [historyError, setHistoryError] = useState(false);
  const [vocabLimit, setVocabLimit] = useState(20), [newWord, setNewWord] = useState(""), [newTranslation, setNewTranslation] = useState("");
  const [languageAction, setLanguageAction] = useState<LanguageActionPrefill | null>(null);
  const actionCurrent = useRef<() => boolean>(() => false);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const busy = useRef(false), mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; historyReqRef.current++; }; }, []);
  const closeEdit = () => { if (!busy.current) { setEditProfile(null); setConfirmDelete(false); setSaveError(false); } };
  const closeHistory = () => { if (!busy.current) { historyReqRef.current++; setHistoryProfile(null); } };
  const openLanguageAction = (action: 'save' | 'delete', resource: LanguageResource, target: LanguageTarget, fields: Partial<LanguageFields> = {}) => {
    if (!learning.userId || busy.current || languageAction) return;
    const current = learning.capture();
    try {
      const operation = crypto.randomUUID();
      const draft = parseLanguageActionPrefill({ action, resource, target, fields, operation_id: operation, action_id: 'language-module-' + operation,
        expected_user_id: learning.userId, origin_kind: 'module' });
      actionCurrent.current = () => mounted.current && current(); setLanguageAction(draft); setSaveError(false);
    } catch { setSaveError(true); }
  };

  // ── Per-language history sheet (sessions read-only + vocabulary w/ delete) ──
  const [historyProfile, setHistoryProfile] = useState<LanguageProfile | null>(null);
  const [historySessions, setHistorySessions] = useState<LanguageSessionRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Guards out-of-order resolutions: rapidly opening profile A then B must
  // never leave A's sessions rendered under B's header (review 03/07).
  const historyReqRef = useRef(0);
  const openLangHistory = useCallback(async (prof: LanguageProfile) => {
    setEditProfile(null);
    setConfirmDelete(false);
    setHistoryProfile(prof);
    setHistorySessions([]);
    setHistoryLoading(true);
    const ticket = ++historyReqRef.current;
    const current = learning.capture(); setHistoryError(false); setSaveError(false); setVocabLimit(20); setRevealed(new Set()); setNewWord(""); setNewTranslation("");
    try { const rows = await getSessionsHistory(prof.learning_language); if (!mounted.current || !current() || historyReqRef.current !== ticket) return; setHistorySessions(rows); }
    catch { if (mounted.current && current() && historyReqRef.current === ticket) setHistoryError(true); }
    finally { if (mounted.current && current() && historyReqRef.current === ticket) setHistoryLoading(false); }
  }, [getSessionsHistory, learning.capture]);

  const sessionTypeLabel = useCallback((type: string): string => {
    if (type === "lesson") return t("language.history.lesson");
    if (type === "tutor_live") return t("language.history.tutor");
    return type;
  }, [t]);

  const { isPremium } = useSubscription();
  const hasProfiles = profiles.length > 0;
  const userLangIds = profiles.map(p => p.learning_language);

  // MODO PROFESSOR → Live tutor (/language/live). Gate Premium delegado a
  // LIVE_REQUIRES_PREMIUM (@/lib/liveAccess — hoje false: Free entra e usa;
  // com true o paywall abaixo volta sozinho). Segue exigindo um perfil de
  // idioma para o tutor sempre ter língua-alvo (null-guard).
  const openProfessorLive = useCallback(() => {
    if (LIVE_REQUIRES_PREMIUM && !isPremium) {
      analytics.paywallViewed("language_tutor");
      navigate("/pricing");
      return;
    }
    if (!hasProfiles) {
      toast(t("language.professor.needLanguage"));
      navigate("/language/setup");
      return;
    }
    // language_live_opened is fired by the /language/live route's own mount
    // effect (LanguageLive.tsx) — the canonical single source that also covers
    // direct navigation. Firing here too double-counted (same-second dupe), so
    // this call-site is intentionally removed; the navigate is the only action.
    navigate(`/language/live?lang=${learning.profile?.learning_language || ""}`);
  }, [isPremium, hasProfiles, navigate, t, learning.profile?.learning_language]);
  const availableLangs = ALL_LANGUAGES.filter(l => !userLangIds.includes(l.id as LangCode));

  const saveProfile = (patch: Partial<Pick<LanguageProfile, "level" | "daily_goal_minutes">>) => {
    if (!editProfile || busy.current) return;
    // The shared editor reads the current row, merges this suggestion, and requires review.
    openLanguageAction('save', 'profile', { language: editProfile.learning_language }, patch as Partial<LanguageFields>);
    setEditProfile(null); setConfirmDelete(false);
  };
  const handleChangeLevel = (level: LanguageProfile["level"]) => saveProfile({ level });
  const handleChangeGoal = (daily_goal_minutes: number) => saveProfile({ daily_goal_minutes });
  const handleDelete = async () => {
    if (!editProfile || busy.current) return; const current = learning.capture(); busy.current = true; setSaving(true); setSaveError(false);
    try { const ok = await deleteProfile(editProfile.learning_language); if (!mounted.current || !current()) return; if (!ok) { setSaveError(true); return; } setConfirmDelete(false); setEditProfile(null); }
    catch { if (mounted.current && current()) setSaveError(true); }
    finally { busy.current = false; if (mounted.current) setSaving(false); }
  };
  const activeProfile = learning.profile;
  const activeLanguage = activeProfile ? ALL_LANGUAGES.find(l => l.id === activeProfile.learning_language) : null;
  const weakWords = activeProfile ? selectReviewWords(learning.getVocabularyForLanguage(activeProfile.learning_language)) : [];
  const recordedSessions = learning.sessions.filter(session => session.language === activeProfile?.learning_language);

  if (profileLoading) {
    return (
      <MobileLayout className="language-page pb-28 pt-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </MobileLayout>
    );
  }

  return (
    <MobileLayout className="language-page pb-28 pt-8 relative overflow-hidden">
      {!languageAction && <LanguagePendingRecoveryPanel refreshKey="language-module" />}
      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {PARTICLES.map((p, i) => (
          <motion.div key={i} className="absolute rounded-full"
            style={{ left: p.left, top: p.top, width: p.w, height: p.w, background: p.color, opacity: 0.17, willChange: "transform, opacity" }}
            animate={reduceMotion ? undefined : { y: [0, p.yD, 0], x: [0, p.xD, 0], opacity: [0.15, 0.2, 0.15] }}
            transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }} />
        ))}
        <motion.div className="absolute w-[280px] h-[280px] rounded-full"
          style={{ top: -80, right: -80, background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, transparent 70%)", willChange: "transform, opacity" }}
          animate={reduceMotion ? undefined : { opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.05, 0.95] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <MirrorBackButton />
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.15)" }}>
          <Star size={12} style={{ color: "hsl(187 72% 55%)" }} />
          <span className="text-[11px] font-display font-bold" style={{ color: "hsl(187 72% 55%)" }}>{learning.sessions.length} {t("language.history.sessions")}</span>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("language.subtitle")}</p>
        <div className="flex items-center gap-2">
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("language.title")}</h1>
          <InfoTooltip title={t("tooltips.language.title")} description={t("tooltips.language.description")} />
        </div>
      </motion.div>

      {learning.loadError && <section role="alert" className="language-status"><p>{t("language.ux.stale")}</p><button disabled={learning.isRefreshing} onClick={() => void refresh()}>{t("language.ux.retry")}</button></section>}
      <section className="language-teacher-heading"><p className="language-eyebrow">{t('language.game.teacherLabel')}</p><h2>{t('language.game.teacherTitle')}</h2><p>{t('language.game.teacherHint')}</p>{activeLanguage && <p className="mt-3 font-semibold">{activeLanguage.emoji} {activeLanguage.label}</p>}{profiles.length > 1 && <label className="language-input-label mt-3">{t('language.myLanguages')}<select className="bg-background border border-border rounded-xl p-3 text-base min-h-12" value={activeProfile?.learning_language} disabled={saving} onChange={async e => { const code = e.target.value as LangCode; setSaving(true); setSaveError(false); const current = learning.capture(); const ok = await learning.updateProgress({active_language:code}); if (mounted.current && current()) { setSaving(false); setSaveError(!ok); } }}>{profiles.map(p => <option value={p.learning_language} key={p.learning_language}>{ALL_LANGUAGES.find(l => l.id === p.learning_language)?.label}</option>)}</select></label>}{saveError && !editProfile && !historyProfile && <p role="alert">{t('language.ux.saveError')}</p>}</section>
      {/* Avatar Teacher → MODO PROFESSOR live tutor (gate: LIVE_REQUIRES_PREMIUM). */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(0)}
        className="mb-5 cursor-pointer active:scale-[0.99] transition-transform relative"
        role="button"
        tabIndex={0}
        aria-label={t("language.avatarTeacher.label")}
        onClick={openProfessorLive}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openProfessorLive(); } }}
      >
        <AvatarTeacher />
        {/* Tap-to-talk affordance — makes the stage's tap-to-converse gesture
            discoverable. Overlay on the Idioma screen (sibling of AvatarTeacher),
            NOT inside the avatar renderer. pointer-events-none lets taps fall
            through to openProfessorLive; it naturally disappears on navigation. */}
        <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", border: "1px solid hsl(187 72% 50% / 0.35)", boxShadow: "0 0 16px hsl(187 72% 50% / 0.15)" }}>
            <motion.span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(187 72% 55%)" }}
              animate={reduceMotion ? undefined : { opacity: [0.4, 1, 0.4], scale: [0.8, 1.15, 0.8] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
            <span className="text-[10px] font-display font-semibold tracking-wide" style={{ color: "hsl(187 72% 72%)" }}>
              {t("language.avatarTeacher.tapToTalk")}
            </span>
          </div>
        </div>
      </motion.div>

      {activeProfile && <LanguageClassPlanner key={activeProfile.learning_language} learning={learning} profile={activeProfile} onTalk={openProfessorLive} />}
      {activeProfile && activeLanguage && <section className="language-next">
        <p>{t("language.ux.nextStep")}</p><h2>{activeLanguage.emoji} {activeLanguage.label}</h2>
        <p>{recordedSessions.length} {t("language.ux.sessionsRecorded")} · {activeProfile.daily_goal_minutes} {t("common.min")} / {t("language.ux.day")}</p>
        <div className="language-next-actions"><button className="language-action" onClick={() => navigate(`/language/${activeProfile.learning_language}`)}>{t("language.ux.continue")}</button><button className="language-action" onClick={() => navigate(`/language/${activeProfile.learning_language}?practice=review`)}>{t("language.game.homework")} · {weakWords.length}</button></div>
        <p className="mt-3">{t("language.ux.reviewHint")}</p>
      </section>}
      {/* ─── YOUR LANGUAGES ─── */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}>
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
          {t("language.myLanguages")}
        </p>

        {!hasProfiles ? (
          <div className="rounded-2xl p-6 text-center mb-5" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.15)" }}>
            <Sparkles size={28} className="mx-auto mb-3" style={{ color: "hsl(187 72% 50% / 0.4)" }} />
            <p className="font-display text-[15px] font-bold text-foreground mb-1">{t("language.home.emptyTitle")}</p>
            <p className="text-[12px] font-body text-foreground/45 leading-relaxed mb-4">{t("language.home.emptyText")}</p>
            <button onClick={() => navigate("/language/setup")}
              className="w-full py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide active:scale-[0.97]"
              style={{ background: "hsl(187 72% 50%)", color: "#0a0a0f" }}>
              {t("language.home.startLearning")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            {profiles.map(prof => {
              const langInfo = ALL_LANGUAGES.find(l => l.id === prof.learning_language);
              if (!langInfo) return null;
              const level = LEVEL_LABELS[prof.level] || prof.level;
              const completed = new Set(learning.sessions.filter(session => session.language === prof.learning_language && session.session_type === "lesson" && session.lesson_id).map(session => session.lesson_id)).size;
              return (
                <div key={prof.learning_language} className="relative">
                  <button onClick={() => navigate(`/language/${prof.learning_language}`)}
                    className="w-full rounded-2xl text-left active:scale-[0.97] transition-all hover:scale-[1.01] relative overflow-hidden"
                    style={{ minHeight: 150, border: `1.5px solid hsl(187 72% 50% / 0.35)`, boxShadow: "0 0 20px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(187 72% 50% / 0.08)" }}>
                    <div className="absolute inset-0" style={{ backgroundImage: `url(${langInfo.bg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.3) 100%)" }} />
                    <div className="relative p-4 flex flex-col justify-end h-full" style={{ minHeight: 150, zIndex: 1 }}>
                      <span className="text-[36px] leading-none mb-2" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))" }}>{langInfo.emoji}</span>
                      <p className="font-display text-[15px] font-bold text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{langInfo.label}</p>
                      <span className="text-[9px] font-display font-bold px-1.5 py-0.5 rounded-full mt-1 inline-block w-fit"
                        style={{ background: "hsl(187 72% 50% / 0.2)", color: "hsl(187 72% 70%)", border: "1px solid hsl(187 72% 50% / 0.3)" }}>
                        {level}
                      </span>
                      <p className="text-xs font-body mt-2 text-white/80">{completed} {t("language.ux.lessonsCompleted")}</p>
                    </div>
                  </button>
                  {/* Settings icon */}
                  <button onClick={(e) => { e.stopPropagation(); setSaveError(false); setEditProfile(prof); }}
                    aria-label={`${t("language.ux.manage")}: ${langInfo.label}`} className="absolute top-2 right-2 w-11 h-11 rounded-full flex items-center justify-center z-10 active:scale-90"
                    style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
                    <MoreVertical size={13} className="text-white/70" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* ─── AVAILABLE LANGUAGES ─── */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)}>
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
          {t("language.home.availableLanguages")}
        </p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {availableLangs.map((lang, i) => (
            <motion.button key={lang.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.04, duration: 0.4 }}
              onClick={() => navigate(`/language/setup?lang=${lang.id}`)}
              className="flex items-center gap-3 rounded-xl p-3 active:scale-[0.97] transition-all relative overflow-hidden group"
              style={{ background: lang.accent, border: `1px solid hsl(var(--border) / 0.25)`, borderLeft: `3px solid ${lang.accentBorder}` }}>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{ background: `radial-gradient(circle at center, ${lang.accent} 0%, transparent 70%)` }} />
              <span className="text-[26px] leading-none relative">{lang.emoji}</span>
              <div className="flex-1 min-w-0 relative">
                <p className="font-display text-[13px] font-semibold text-foreground/70 truncate">{lang.label}</p>
              </div>
              <Plus size={14} className="relative" style={{ color: "hsl(187 72% 50% / 0.4)" }} />
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* ─── MIRROR INSIGHT ─── */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}
        className="rounded-2xl p-5 mb-4 relative overflow-hidden"
        style={{ background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.70)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_015.jpeg) center/cover no-repeat", border: "1.5px solid hsl(187 72% 50% / 0.25)", boxShadow: "0 0 20px hsl(187 72% 50% / 0.06)" }}>
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.1)", border: "1px solid hsl(187 72% 50% / 0.2)" }}>
            <Star size={14} style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("language.home.mirrorInsight")}</p>
            <p className="text-[12px] font-body text-foreground/60 leading-relaxed italic mb-3">{t("language.home.mirrorInsightText")}</p>
            <button onClick={() => navigate("/mirror")} className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100" style={{ color: "hsl(187 72% 55%)" }}>
              <span>{t("language.discussGoals")}</span><ArrowRight size={11} />
            </button>
          </div>
        </div>
      </motion.div>
      )}

      {/* ─── EDIT MODAL ─── */}
      <AnimatePresence>
        {editProfile && (
          <AgendaBottomSheet open dismissDisabled={saving} onClose={closeEdit} title={ALL_LANGUAGES.find(l => l.id === editProfile.learning_language)?.label || t("language.title")} footer={null}>
            <div className="language-sheet">
              {/* Handle */}
              <div className="w-10 h-1 rounded-full mx-auto mb-4 shrink-0" style={{ background: "hsl(var(--border) / 0.5)" }} />

              {/* Header */}
              <div className="flex items-center justify-between mb-5 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-[24px]">{ALL_LANGUAGES.find(l => l.id === editProfile.learning_language)?.emoji}</span>
                  <p className="font-display text-[16px] font-bold text-foreground">
                    {ALL_LANGUAGES.find(l => l.id === editProfile.learning_language)?.label}
                  </p>
                </div>
                <button disabled={saving} aria-label={t("common.close")} onClick={closeEdit}
                  className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "hsl(0 0% 100% / 0.06)" }}>
                  <X size={14} className="text-foreground/60" />
                </button>
              </div>

              {/* Scrollable region. `min-h-0` is the load-bearing fix: in a
                  max-height flex column, WebKit (iOS WKWebView — this app runs in
                  Capacitor) does NOT auto-resolve a flex child's `min-height:auto`
                  to 0 for scroll containers the way Blink/Chromium does. Without it
                  the child keeps its full content height, the sheet spills below the
                  viewport, and the red remove button is unreachable — no scroll. This
                  is why #377 (85dvh + overflow-y-auto, mirroring the history sheet)
                  looked correct in Chromium but failed on device. safe-area pad keeps
                  the last button clear of the home indicator. */}
              <div className="overflow-y-auto min-h-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>

              {saveError && <p role="alert" className="language-status">{t("language.ux.saveError")}</p>}
              {/* Change Level */}
              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                {t("language.manage.changeLevel")}
              </p>
              <div className="flex gap-2 mb-5">
                {LEVELS.map(lvl => (
                  <button disabled={saving} aria-pressed={editProfile.level === lvl} key={lvl} onClick={() => handleChangeLevel(lvl)}
                    className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold transition-all active:scale-[0.96]"
                    style={{
                      background: editProfile.level === lvl ? "hsl(187 72% 50% / 0.15)" : "hsl(var(--card))",
                      border: `1.5px solid ${editProfile.level === lvl ? "hsl(187 72% 50% / 0.5)" : "hsl(var(--border) / 0.3)"}`,
                      color: editProfile.level === lvl ? "hsl(187 72% 55%)" : "hsl(var(--foreground) / 0.6)",
                    }}>
                    {LEVEL_LABELS[lvl]}{editProfile.level === lvl && <Check size={10} className="inline ml-1" />}
                  </button>
                ))}
              </div>

              {/* Daily Goal */}
              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                {t("language.manage.dailyGoal")}
              </p>
              <div className="flex gap-2 mb-5">
                {TIME_OPTIONS.map(m => (
                  <button disabled={saving} aria-pressed={editProfile.daily_goal_minutes === m} key={m} onClick={() => handleChangeGoal(m)}
                    className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold transition-all active:scale-[0.96]"
                    style={{
                      background: editProfile.daily_goal_minutes === m ? "hsl(187 72% 50% / 0.15)" : "hsl(var(--card))",
                      border: `1.5px solid ${editProfile.daily_goal_minutes === m ? "hsl(187 72% 50% / 0.5)" : "hsl(var(--border) / 0.3)"}`,
                      color: editProfile.daily_goal_minutes === m ? "hsl(187 72% 55%)" : "hsl(var(--foreground) / 0.6)",
                    }}>
                    {m}{m === 30 ? "+" : ""} {t("fitness.min")}
                  </button>
                ))}
              </div>

              {/* History (sessions + vocabulary) */}
              <button onClick={() => editProfile && openLangHistory(editProfile)}
                className="w-full py-3 rounded-xl font-display text-[12px] font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.97] mb-3"
                style={{ background: "hsl(187 72% 50% / 0.08)", color: "hsl(187 72% 55%)", border: "1px solid hsl(187 72% 50% / 0.2)" }}>
                <History size={13} />{t("language.history.button")}
              </button>

              {/* Remove Language */}
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)}
                  className="w-full py-3 rounded-xl font-display text-[12px] font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.97]"
                  style={{ background: "hsl(0 70% 50% / 0.08)", color: "hsl(0 70% 60%)", border: "1px solid hsl(0 70% 50% / 0.2)" }}>
                  <Trash2 size={13} />{t("language.manage.remove")}
                </button>
              ) : (
                <div className="rounded-xl p-4" style={{ background: "hsl(0 70% 50% / 0.08)", border: "1px solid hsl(0 70% 50% / 0.25)" }}>
                  <p className="text-[12px] font-body text-foreground/70 mb-3">{t("language.manage.removeConfirm")}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-medium"
                      style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border) / 0.3)", color: "hsl(var(--foreground) / 0.7)" }}>
                      {t("language.manage.cancel")}
                    </button>
                    <button onClick={handleDelete} disabled={saving}
                      className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold active:scale-[0.96]"
                      style={{ background: "hsl(0 70% 50%)", color: "white" }}>
                      {saving ? "..." : t("language.manage.confirmDelete")}
                    </button>
                  </div>
                </div>
              )}
              </div>

              {saving && <div className="absolute inset-0 flex items-center justify-center rounded-t-2xl" style={{ background: "rgba(0,0,0,0.3)" }}>
                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>}
            </div>
          </AgendaBottomSheet>
        )}
      </AnimatePresence>

      {/* ─── HISTORY SHEET — sessions (read-only) + vocabulary (delete+undo) ─── */}
      <AnimatePresence>
        {historyProfile && (
          <AgendaBottomSheet open dismissDisabled={!!languageAction} onClose={closeHistory} title={t("language.history.button")} footer={null}>
            <div className="language-sheet">
              <div className="w-10 h-1 rounded-full mx-auto mb-4 shrink-0" style={{ background: "hsl(var(--border) / 0.5)" }} />

              <div className="flex items-center justify-between mb-4 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-[24px]">{ALL_LANGUAGES.find(l => l.id === historyProfile.learning_language)?.emoji}</span>
                  <p className="font-display text-[16px] font-bold text-foreground">
                    {t("language.history.button")} · {ALL_LANGUAGES.find(l => l.id === historyProfile.learning_language)?.label}
                  </p>
                </div>
                <button disabled={!!languageAction} aria-label={t("common.close")} onClick={closeHistory}
                  className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "hsl(0 0% 100% / 0.06)" }}>
                  <X size={14} className="text-foreground/60" />
                </button>
              </div>

              <div className="overflow-y-auto" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
                {/* Sessions — read-only chronological list (deleting a session
                    would re-lock lessons whose completion derives from it) */}
                <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                  {t("language.history.sessions")}
                </p>
                {historyError ? <section role="alert" className="language-status"><p>{t("language.ux.loadError")}</p><button onClick={() => void openLangHistory(historyProfile)}>{t("language.ux.retry")}</button></section> : historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : (
                  <ModuleHistoryList<LanguageSessionRow>
                    items={historySessions}
                    getId={(s) => s.id}
                    getDate={(s) => s.created_at}
                    renderItem={(s) => (
                      <p className="text-sm text-white">
                        {sessionTypeLabel(s.session_type) +
                          " · " + Math.max(1, Math.round(s.duration_seconds / 60)) + " " + t("fitness.min") +
                          (s.xp_earned ? " · +" + s.xp_earned + " XP" : "")}
                      </p>
                    )}
                    labels={{
                      empty: t("language.history.sessionsEmpty"),
                      deletedToast: t("moduleHistory.deletedToast"),
                      undo: t("moduleHistory.undo"),
                      period30: t("moduleHistory.period30"),
                      period90: t("moduleHistory.period90"),
                      periodAll: t("moduleHistory.periodAll"),
                    }}
                  />
                )}

                <h2 className="text-base font-semibold mt-6">{t("language.history.vocabulary")}</h2>
                <p className="text-sm text-muted-foreground mt-2">{t("language.ux.reviewHint")}</p>
                <fieldset className="min-w-0 space-y-3 my-4" disabled={!!languageAction}>
                  <legend className="font-semibold">{t('languageAction.addWord')}</legend>
                  <label className="block space-y-1"><span>{t('languageAction.field.word')}</span><input maxLength={300} className="min-h-11 w-full rounded-xl border border-border bg-background px-3 py-2" value={newWord} onChange={e => setNewWord(e.target.value)} /></label>
                  <label className="block space-y-1"><span>{t('languageAction.field.translation')}</span><input maxLength={300} className="min-h-11 w-full rounded-xl border border-border bg-background px-3 py-2" value={newTranslation} onChange={e => setNewTranslation(e.target.value)} /></label>
                  <button className="language-action" disabled={!newWord.trim() || !newTranslation.trim()} onClick={() => openLanguageAction('save', 'vocabulary', { language: historyProfile.learning_language, word: newWord.trim() }, { translation: newTranslation.trim() })}>{t('languageAction.review')}</button>
                </fieldset>
                {saveError && <p role="alert" className="language-status">{t("language.ux.saveError")}</p>}
                {getVocabularyForLanguage(historyProfile.learning_language).slice().sort((a,b) => a.mastery - b.mastery).slice(0,vocabLimit).map(word => <article key={word.id} className="language-vocabulary">
                  <h3>{word.word}</h3>
                  {revealed.has(word.id) ? <p>{word.translation}</p> : <button className="language-action" onClick={() => setRevealed(previous => new Set([...previous,word.id]))}>{t("language.ux.showTranslation")}</button>}
                  <p className="text-muted-foreground">{t("language.history.mastery")} {word.mastery}%</p>
                  <div className="flex flex-wrap gap-3"><button disabled={!!languageAction} aria-label={`${t("languageAction.editWord")}: ${word.word}`} onClick={() => openLanguageAction('save', 'vocabulary', { language: word.language, word: word.word })}>{t("languageAction.editWord")}</button>
                    <button disabled={!!languageAction} aria-label={`${t("common.delete")}: ${word.word}`} onClick={() => openLanguageAction('delete', 'vocabulary', { language: word.language, word: word.word })}>{t("common.delete")}</button></div>
                </article>)}
                {!getVocabularyForLanguage(historyProfile.learning_language).length && <p className="language-status">{t("language.history.vocabEmpty")}</p>}
                {getVocabularyForLanguage(historyProfile.learning_language).length > vocabLimit && <button className="language-action" onClick={() => setVocabLimit(value => value + 20)}>{t("language.ux.showMore")}</button>}

              </div>
            </div>
          </AgendaBottomSheet>
        )}
      </AnimatePresence>

      <LanguageActionModal open={!!languageAction} prefill={languageAction} isCurrent={() => actionCurrent.current()} onClose={() => setLanguageAction(null)}
        onCreated={async (receipt, current) => {
          if (!current() || !actionCurrent.current() || receipt.userId !== learning.userId || receipt.origin_context.kind !== 'module') return false;
          await refresh(); if (!current() || !actionCurrent.current()) return false;
          setNewWord(""); setNewTranslation(""); return true;
        }} />
    </MobileLayout>
  );
};

const Language: React.FC = () => {
  const learning = useLanguageLearning();
  if (!learning.userId || !learning.hasData) return <LanguageState learning={learning} />;
  return <MotionConfig reducedMotion="user"><LanguageContent key={learning.userId} learning={learning} /></MotionConfig>;
};
export default Language;
